import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  Paper,
} from '@mui/material';
import { Fade } from '@mui/material';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';

const ZonalAdmin = () => {
  const { t } = useAdminLanguage();
  const [settings, setSettings] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/settings', { headers: getAuthHeaders() });
        if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
        const data = await response.json();
        setSettings(data || {});
      } catch (err) {
        console.error('Erreur lors de la récupération des données:', err);
        setError('Une erreur est survenue lors du chargement des données.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    setError(null);

    try {
      const response = await fetch('/api/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        setMessage('Données mises à jour avec succès !');
      } else {
        const data = await response.json();
        setError(data.error || 'Erreur lors de la mise à jour des données.');
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour:', err);
      setError('Erreur lors de la communication avec le serveur.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in={!loading} timeout={500}>
        <Box component="form" onSubmit={handleSubmit}>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Préférences Zonales')}
          </Typography>

          <Info type="zonal" />

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              {/* Champs communs */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
                  {t('Données Communes')}
                </Typography>
                
                <TextField
                  fullWidth
                  label={t('N° de Client')}
                  name="clientNumber"
                  value={settings.clientNumber || ''}
                  disabled
                  variant="outlined"
                  margin="normal"
                />
                
                <TextField
                  fullWidth
                  label={t('Nom de Client')}
                  name="clientName"
                  value={settings.clientName || ''}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  margin="normal"
                  disabled={submitting}
                />
                
                <TextField
                  fullWidth
                  label={t('Version de BlueNote')}
                  name="version"
                  value={settings.version || ''}
                  disabled
                  variant="outlined"
                  margin="normal"
                />

                <FormControl component="fieldset" sx={{ my: 2 }}>
                  <FormLabel component="legend">{t('Type')}</FormLabel>
                  <RadioGroup
                    row
                    name="type"
                    value={settings.type || ''}
                    onChange={handleChange}
                  >
                    <FormControlLabel
                      value="local"
                      control={<Radio />}
                      label={t('Locale')}
                      disabled={submitting}
                    />
                    <FormControlLabel
                      value="federal"
                      control={<Radio />}
                      label={t('Fédérale')}
                      disabled={submitting}
                    />
                  </RadioGroup>
                </FormControl>

                <TextField
                  fullWidth
                  label={t('Numéro de la Zone')}
                  name="zoneNumber"
                  value={settings.zoneNumber || ''}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  margin="normal"
                  disabled={submitting}
                />
                
                <TextField
                  fullWidth
                  label={t('Arrondissement Judiciaire (Court)')}
                  name="shortJudicialDistrict"
                  value={settings.shortJudicialDistrict || ''}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  margin="normal"
                  disabled={submitting}
                />
                
                <TextField
                  fullWidth
                  label={t('N° de Division Judiciaire')}
                  name="judicialDivision"
                  value={settings.judicialDivision || ''}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  margin="normal"
                  disabled={submitting}
                />
              </Box>

              {/* Données multilingues */}
              <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
                {t('Données Multilingues')}
              </Typography>
              
              <Grid container spacing={3}>
                {/* Colonne Français */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, height: 'fit-content' }}>
                    <Typography variant="h6" gutterBottom align="center" sx={{ mb: 3, color: 'primary.main' }}>
                      {t('Version en français')}
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label={t('Nom de la Zone')}
                      name="zoneName"
                      value={settings.zoneName || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Adresse de la Zone')}
                      name="zoneAddress"
                      value={settings.zoneAddress || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Arrondissement Judiciaire')}
                      name="judicialDistrict"
                      value={settings.judicialDistrict || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Nom de Zone dans les Procès-Verbaux')}
                      name="zoneNamePV"
                      value={settings.zoneNamePV || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Nom du dispatching')}
                      name="dispatching"
                      value={settings.dispatching || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Service dépôt pièces à conviction')}
                      name="seizureService"
                      value={settings.seizureService || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Nom du chef de corps')}
                      name="chiefOfCorps"
                      value={settings.chiefOfCorps || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Lieu de rédaction par défaut')}
                      name="defaultLocation"
                      value={settings.defaultLocation || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Nom du CPVS')}
                      name="cpvsName"
                      value={settings.cpvsName || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Lieu du CPVS')}
                      name="cpvsLocation"
                      value={settings.cpvsLocation || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Distributeur d\'eau')}
                      name="waterDistributor"
                      value={settings.waterDistributor || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Société gestionnaire des transports en commun')}
                      name="publicTransportCompany"
                      value={settings.publicTransportCompany || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Gestionnaire de réseau de distribution (GRD)')}
                      name="distributionNetworkManager"
                      value={settings.distributionNetworkManager || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Intercommunale de gestion des déchets')}
                      name="wasteManagementIntercommunal"
                      value={settings.wasteManagementIntercommunal || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                  </Paper>
                </Grid>

                {/* Colonne Néerlandais */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, height: 'fit-content' }}>
                    <Typography variant="h6" gutterBottom align="center" sx={{ mb: 3, color: 'primary.main' }}>
                      {t('Version en néerlandais')}
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label={t('Nom de la Zone')}
                      name="zoneNameNL"
                      value={settings.zoneNameNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Adresse de la Zone')}
                      name="zoneAddressNL"
                      value={settings.zoneAddressNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Arrondissement Judiciaire')}
                      name="judicialDistrictNL"
                      value={settings.judicialDistrictNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Nom de Zone dans les PV')}
                      name="zoneNamePVNL"
                      value={settings.zoneNamePVNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Nom du dispatching')}
                      name="dispatchingNL"
                      value={settings.dispatchingNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Service dépôt pièces à conviction')}
                      name="seizureServiceNL"
                      value={settings.seizureServiceNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Nom du chef de corps')}
                      name="chiefOfCorpsNL"
                      value={settings.chiefOfCorpsNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Lieu de rédaction par défaut')}
                      name="defaultLocationNL"
                      value={settings.defaultLocationNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Nom du CPVS')}
                      name="cpvsNameNL"
                      value={settings.cpvsNameNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Lieu du CPVS')}
                      name="cpvsLocationNL"
                      value={settings.cpvsLocationNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Distributeur d\'eau')}
                      name="waterDistributorNL"
                      value={settings.waterDistributorNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Société gestionnaire des transports en commun')}
                      name="publicTransportCompanyNL"
                      value={settings.publicTransportCompanyNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Gestionnaire de réseau de distribution (GRD)')}
                      name="distributionNetworkManagerNL"
                      value={settings.distributionNetworkManagerNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('Intercommunale de gestion des déchets')}
                      name="wasteManagementIntercommunalNL"
                      value={settings.wasteManagementIntercommunalNL || ''}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                      disabled={submitting}
                    />
                  </Paper>
                </Grid>
              </Grid>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={submitting}
                startIcon={submitting && <CircularProgress size={20} />}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  textTransform: 'none',
                  transition: 'all 0.3s',
                  '&:hover': { boxShadow: 6 },
                  maxWidth: '300px',
                  mx: 'auto',
                  display: 'block',
                  mt: 4,
                }}
              >
                {submitting ? t('Enregistrement...') : t('Enregistrer')}
              </Button>

              {message && (
                <Alert severity="success" sx={{ mt: 2, maxWidth: '500px', mx: 'auto' }}>
                  {message}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2, maxWidth: '500px', mx: 'auto' }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default ZonalAdmin;